import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  CContainer, 
  CRow, 
  CCol, 
  CButton, 
  CBadge, 
  CSpinner,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter
} from '@coreui/react';
import api from '../../api/axiosInstance';

const ChapterWiseQuestionBank = () => {
  const { section, title } = useParams();
  const navigate = useNavigate();

  const [chaptersData, setChaptersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal State
  const [modalVisible, setModalVisible] = useState(false);
  const [modalConfig, setModalConfig] = useState({ title: '', message: '', type: 'auth' });

  // Auth & Subscription Details
  const authToken = localStorage.getItem('authToken');
  const userTier = localStorage.getItem('subscriptionTier') || 'Alpha';
  const isSubActive = localStorage.getItem('isSubscriptionActive') === 'true';

  // Allowed Tiers Hierarchy for Access
  const allowedTiers = ['Beeta', 'Gamma', 'Delta', 'Sigma'];
  const hasBeetaAccess = isSubActive && allowedTiers.includes(userTier);

  const cleanTitle = title ? title.replace(/-/g, ' ') : '';

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchChapterWiseData();
  }, [section, title]);

  const fetchChapterWiseData = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await api.get(
        `/api/content/GetChapterWisePapers?section=${section}&category=${encodeURIComponent(cleanTitle)}`,
        { skipAuthCheck: true }
      );

      if (res.data) {
        setChaptersData(res.data.chapters || res.data || []);
      }
    } catch (err) {
      console.error("Error fetching chapterwise data:", err);
      setError("Failed to load chapter-wise topics.");
    } finally {
      setLoading(false);
    }
  };

  const handleTopicClick = (topic) => {
    const topicSlug = topic.title ? topic.title.replace(/\s+/g, '-').toLowerCase() : topic.id;
    const targetUrl = `/${section}/${title}/topic/${topicSlug}`;

    // 1. LOGIN CHECK
    if (!authToken) {
      setModalConfig({
        title: 'Authentication Required 🔒',
        message: 'Is chapter ke questions access karne ke liye Login zaroori hai.',
        type: 'auth'
      });
      setModalVisible(true);
      return;
    }

    // 2. BEETA SUBSCRIPTION CHECK
    if (!hasBeetaAccess) {
      setModalConfig({
        title: 'Beeta Subscription Required 👑',
        message: 'To unlock this content, you must have an active Beta subscription plan.',
        type: 'upgrade'
      });
      setModalVisible(true);
      return;
    }

    // Access Granted
    navigate(targetUrl);
  };

  const handleModalConfirm = () => {
    setModalVisible(false);
    if (modalConfig.type === 'auth') {
      navigate('/login');
    } else {
      navigate('/profile');
    }
  };

  if (loading) {
    return (
      <div className="text-center mt-5 py-5">
        <CSpinner color="primary" size="lg" />
        <p className="text-muted mt-2">Loading Chapters & Topics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <CContainer className="text-center mt-5 py-4">
        <h5 className="text-danger mb-3">{error}</h5>
        <CButton color="primary" size="sm" onClick={() => navigate(-1)}>Go Back</CButton>
      </CContainer>
    );
  }

  return (
    <div style={{ overflowX: 'hidden', width: '100%' }}>
      {/* 🌟 FULL-WIDTH EDGE-TO-EDGE BLUE BANNER */}
      <div 
        className="text-white text-center py-5 shadow-sm mb-5" 
        style={{ 
          backgroundColor: '#0056b3',
          width: '100vw',
          position: 'relative',
          left: '50%',
          transform: 'translateX(-50%)'
        }}
      >
        <div className="container py-2 px-3">
          <span className="text-warning fw-bold text-uppercase" style={{ fontSize: '13px', letterSpacing: '1px' }}>
            {section?.toUpperCase()} CHAPTERWISE MODULE
          </span>
          <h1 className="fw-bold m-0 text-white mt-1 display-6">
            {cleanTitle.toUpperCase()} Previous Year Questions
          </h1>
          <div className="mt-3">
            <CBadge color="light" className="text-primary px-3 py-2 fs-6 rounded-pill fw-bold">
              {chaptersData.length} Chapters Available
            </CBadge>
          </div>
        </div>
      </div>

      {/* 🚀 MAIN CONTENT CONTAINER */}
      <CContainer className="pb-5" style={{ maxWidth: '1100px' }}>
        {/* CHAPTERS & TOPICS SECTION */}
        {chaptersData.length > 0 ? (
          chaptersData.map((chapter, idx) => (
            <div key={chapter.id || idx} className="mb-5 bg-white p-4 rounded-3 border shadow-sm">
              
              {/* Chapter Header */}
              <div className="d-flex align-items-center justify-content-between mb-4 pb-2 border-bottom">
                <div className="d-flex align-items-center gap-2">
                  <div style={{ width: '5px', height: '24px', backgroundColor: '#0f172a', borderRadius: '2px' }}></div>
                  <h4 className="fw-bold text-dark m-0" style={{ fontSize: '1.25rem', letterSpacing: '-0.3px' }}>
                    {chapter.chapterName || chapter.title}
                  </h4>
                </div>
                <span className="badge bg-light text-secondary border fw-normal px-2.5 py-1.5" style={{ fontSize: '0.8rem' }}>
                  {chapter.topics?.length || 0} Topics
                </span>
              </div>

              {/* Topics Grid */}
              <CRow className="g-3">
                {(chapter.topics || []).map((topic) => {
                  const isLocked = !hasBeetaAccess;

                  return (
                    <CCol lg={4} md={6} sm={12} key={topic.id || topic.title}>
                      <div 
                        className="h-100 rounded-3 overflow-hidden border custom-topic-card d-flex flex-column justify-content-between"
                        style={{ backgroundColor: '#ffffff', cursor: 'pointer', transition: 'all 0.25s ease' }}
                        onClick={() => handleTopicClick(topic)}
                      >
                        {/* Topic Title Area */}
                        <div className="p-3 d-flex align-items-start gap-2.5">
                          <span className="text-primary mt-0.5" style={{ fontSize: '1.1rem' }}>📌</span>
                          <span className="fw-semibold text-dark" style={{ fontSize: '0.95rem', lineHeight: '1.45' }}>
                            {topic.title}
                          </span>
                        </div>

                        {/* Compact Action Footer */}
                        <div 
                          className="py-2 px-3 text-white fw-bold d-flex align-items-center justify-content-between text-center mt-2"
                          style={{ backgroundColor: isLocked ? '#6c757d' : '#0A2540', fontSize: '0.85rem' }}
                        >
                          <span>
                            {isLocked ? "Unlock Topic (Beeta Required)" : "View Questions"}
                          </span>
                          <span>{isLocked ? "🔒" : "➔"}</span>
                        </div>
                      </div>
                    </CCol>
                  );
                })}
              </CRow>

            </div>
          ))
        ) : (
          <div className="alert alert-warning text-center">No chapters found for this category.</div>
        )}

        {/* COREUI MODAL FOR SUBSCRIPTION CHECK */}
        <CModal visible={modalVisible} onClose={() => setModalVisible(false)} alignment="center">
          <CModalHeader className="bg-dark text-white">
            <CModalTitle className="fw-bold fs-5">{modalConfig.title}</CModalTitle>
          </CModalHeader>
          <CModalBody className="py-4">
            <p className="fs-6 text-dark m-0">{modalConfig.message}</p>
          </CModalBody>
          <CModalFooter>
            <CButton color="secondary" onClick={() => setModalVisible(false)}>
              Close
            </CButton>
            <CButton color={modalConfig.type === 'upgrade' ? "success" : "primary"} className="text-white fw-bold" onClick={handleModalConfirm}>
              {modalConfig.type === 'upgrade' ? "Upgrade Plan Now" : "Login Now"}
            </CButton>
          </CModalFooter>
        </CModal>

        {/* STYLES */}
        <style>{`
          .custom-topic-card {
            box-shadow: 0 2px 4px rgba(0,0,0,0.04);
          }
          .custom-topic-card:hover {
            transform: translateY(-3px);
            box-shadow: 0 8px 16px rgba(10, 37, 64, 0.12) !important;
            border-color: #0A2540 !important;
          }
        `}</style>
      </CContainer>
    </div>
  );
};

export default ChapterWiseQuestionBank;